/**
 * AWS S3 Upload Utility
 * Handles secure presigned URL uploads directly from browser to S3
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Get presigned URL for uploading a file to S3
 * @param {File} file - The file to upload
 * @param {string} folder - The folder path (e.g., 'images/', 'videos/')
 * @returns {Promise<{upload_url: string, file_url: string, file_key: string}>}
 */
async function getPresignedUrl(file, folder = 'images/') {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_URL}/api/s3/presigned-url`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      filename: file.name,
      content_type: file.type,
      folder: folder,
      file_size: file.size
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get upload URL');
  }

  return response.json();
}

/**
 * Confirm upload completion to backend
 * @param {string} fileKey - S3 object key
 * @param {string} originalFilename - Original file name
 * @param {string} contentType - File content type
 * @param {number} fileSize - File size in bytes
 */
async function confirmUpload(fileKey, originalFilename, contentType, fileSize) {
  const token = localStorage.getItem('token');
  
  const params = new URLSearchParams({
    file_key: fileKey,
    original_filename: originalFilename,
    content_type: contentType,
    file_size: fileSize.toString()
  });

  const response = await fetch(`${API_URL}/api/s3/confirm-upload?${params}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to confirm upload');
  }

  return response.json();
}

/**
 * Upload an image to S3
 * @param {File} file - The image file to upload
 * @param {string} folder - The folder path (default: 'images/')
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {{promise: Promise<{url: string, file_key: string, file_id: string}>, abort: Function}}
 */
export function uploadImageToS3(file, folder = 'images/', onProgress = null) {
  let abortFn = () => {};
  
  const promise = (async () => {
    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Invalid file type. Please upload an image.');
      }

      // Validate file size (10MB max for images)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('Image too large. Maximum size is 10MB.');
      }

      // Step 1: Get presigned URL
      const presigned = await getPresignedUrl(file, folder);

      // Step 2: Upload directly to S3
      const { promise: uploadPromise, abort } = uploadToS3(presigned.upload_url, file, onProgress);
      abortFn = abort;
      
      const uploadResult = await uploadPromise;

      if (!uploadResult.success) {
        throw new Error('Upload failed');
      }

      // Step 3: Confirm upload
      const confirmation = await confirmUpload(
        presigned.file_key,
        file.name,
        file.type,
        file.size
      );

      return {
        url: presigned.file_url,
        file_key: presigned.file_key,
        file_id: confirmation.file_id
      };

    } catch (error) {
      console.error('S3 image upload error:', error);
      throw error;
    }
  })();

  return {
    promise,
    abort: () => abortFn()
  };
}

/**
 * Upload a video to S3
 * @param {File} file - The video file to upload
 * @param {string} folder - The folder path (default: 'videos/')
 * @param {Function} onProgress - Progress callback (percentage, bytesUploaded, bytesTotal)
 * @returns {{promise: Promise<{url: string, file_key: string, file_id: string}>, abort: Function}}
 */
export function uploadVideoToS3(file, folder = 'videos/', onProgress = null) {
  let abortFn = () => {};
  
  const promise = (async () => {
    try {
      // Validate file type
      const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/mpeg', 'video/webm', 'application/pdf'];
      if (!validTypes.includes(file.type) && !file.type.startsWith('video/') && !file.type.startsWith('audio/')) {
        throw new Error('Invalid file type. Please upload a video (MP4, MOV, AVI, MPEG, WebM), audio, or PDF.');
      }

      // Validate file size (5GB max for videos)
      const maxSize = 5 * 1024 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('File too large. Maximum size is 5GB.');
      }

      // Step 1: Get presigned URL
      const presigned = await getPresignedUrl(file, folder);

      // Step 2: Upload directly to S3
      const { promise: uploadPromise, abort } = uploadToS3(presigned.upload_url, file, onProgress);
      abortFn = abort;
      
      const uploadResult = await uploadPromise;

      if (!uploadResult.success) {
        throw new Error('Upload failed');
      }

      // Step 3: Confirm upload
      const confirmation = await confirmUpload(
        presigned.file_key,
        file.name,
        file.type,
        file.size
      );

      return {
        url: presigned.file_url,
        file_key: presigned.file_key,
        file_id: confirmation.file_id
      };

    } catch (error) {
      console.error('S3 video upload error:', error);
      throw error;
    }
  })();

  return {
    promise,
    abort: () => abortFn()
  };
}

/**
 * Get presigned URL for uploading a file to S3 (PUBLIC - no auth required)
 * Used for pre-registration uploads (profile photos, KYC documents)
 * @param {File} file - The file to upload
 * @param {string} folder - The folder path (e.g., 'documents/', 'avatars/', 'registration/')
 * @returns {Promise<{upload_url: string, file_url: string, file_key: string}>}
 */
async function getPublicPresignedUrl(file, folder = 'documents/') {
  const response = await fetch(`${API_URL}/api/s3/presigned-url-public`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      filename: file.name,
      content_type: file.type,
      folder: folder,
      file_size: file.size
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get upload URL');
  }

  return response.json();
}

/**
 * Upload a file to S3 without authentication (for pre-registration uploads)
 * Specifically for KYC documents, profile photos during signup
 * @param {File} file - The file to upload
 * @param {string} folder - The folder path (e.g., 'documents/', 'avatars/', 'registration/')
 * @param {Function} onProgress - Progress callback (percentage, bytesUploaded, bytesTotal)
 * @returns {{promise: Promise<{url: string, file_key: string}>, abort: Function}} The uploaded file URL and key
 */
export function uploadPublicFileToS3(file, folder = 'documents/', onProgress = null) {
  let abortFn = () => {};
  
  const promise = (async () => {
    try {
      // Validate allowed folders for public uploads
      const allowedPublicFolders = ['registration/', 'documents/', 'avatars/'];
      const normalizedFolder = folder.endsWith('/') ? folder : folder + '/';
      
      if (!allowedPublicFolders.some(f => normalizedFolder.startsWith(f))) {
        throw new Error(`Invalid folder for public upload. Allowed: ${allowedPublicFolders.join(', ')}`);
      }

      // Step 1: Get presigned URL (no auth required)
      const presigned = await getPublicPresignedUrl(file, folder);

      // Step 2: Upload directly to S3
      const { promise: uploadPromise, abort } = uploadToS3(presigned.upload_url, file, onProgress);
      abortFn = abort;
      
      const uploadResult = await uploadPromise;

      if (!uploadResult.success) {
        throw new Error('Upload failed');
      }

      // Return both URL and file_key from the presigned response (no URL parsing needed)
      return {
        url: presigned.file_url,
        file_key: presigned.file_key
      };

    } catch (error) {
      console.error('S3 public file upload error:', error);
      throw error;
    }
  })();

  return {
    promise,
    abort: () => abortFn()
  };
}

/**
 * Upload file to S3 using presigned URL with progress tracking
 * @param {string} presignedUrl - The presigned PUT URL
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Progress callback (percentage, bytesUploaded, bytesTotal)
 * @returns {{promise: Promise<{success: boolean}>, abort: Function}}
 */
function uploadToS3(presignedUrl, file, onProgress = null) {
  const xhr = new XMLHttpRequest();
  
  const promise = new Promise((resolve, reject) => {
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        // Pass percentage, bytesUploaded, and bytesTotal for detailed progress tracking
        onProgress(percent, event.loaded, event.total);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ success: true });
      } else {
        console.error('S3 upload failed:', xhr.status, xhr.responseText);
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    // Use PUT method for presigned URL uploads
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });

  return {
    promise,
    abort: () => xhr.abort()
  };
}

/**
 * Delete a file from S3
 * @param {string} fileKey - The S3 object key
 * @returns {Promise<{message: string}>}
 */
export async function deleteFromS3(fileKey) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(
    `${API_URL}/api/s3/delete?file_key=${encodeURIComponent(fileKey)}`,
    {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete file');
  }

  return response.json();
}

/**
 * Get presigned download URL for a private file
 * @param {string} fileKey - The S3 object key
 * @param {string} courseId - Optional course ID for enrollment verification
 * @param {boolean} download - If true, sets Content-Disposition to attachment for download
 * @returns {Promise<{download_url: string, expires_in: number, is_public: boolean}>}
 */
export async function getPresignedDownloadUrl(fileKey, courseId = null, download = false) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  let url = `${API_URL}/api/s3/presigned-download?file_key=${encodeURIComponent(fileKey)}`;
  if (courseId) {
    url += `&course_id=${encodeURIComponent(courseId)}`;
  }
  if (download) {
    url += `&download=1`;
  }

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get download URL');
  }

  return response.json();
}

/**
 * Check if S3 is configured and accessible
 * @returns {Promise<{configured: boolean, bucket: string|null, message: string}>}
 */
export async function checkS3Status() {
  try {
    const response = await fetch(`${API_URL}/api/s3/status`);
    return response.json();
  } catch (error) {
    return { configured: false, bucket: null, message: 'Could not check S3 status' };
  }
}

// Public R2 URL for public assets - read from environment variable
// IMPORTANT: REACT_APP_R2_PUBLIC_URL must be set in production environment variables
const R2_PUBLIC_URL = process.env.REACT_APP_R2_PUBLIC_URL;
if (!R2_PUBLIC_URL) {
  console.warn('REACT_APP_R2_PUBLIC_URL environment variable is not set. File URLs may not work correctly.');
}

// Folders that contain public assets (accessible without auth)
const PUBLIC_FOLDERS = [
  'landing-slots/',
  'products/',
  'images/',
  'avatars/',
  'marketplace/',
];

// Folders that contain private assets (require auth/enrollment)
// NOTE: announcements use presigned URLs due to R2 bucket access restrictions
const PRIVATE_FOLDERS = [
  'courses/',
  'videos/',
  'certificates/',
  'announcements/',
];

/**
 * Upload any file to S3 (generic uploader)
 * @param {File} file - The file to upload
 * @param {string} folder - The folder path (e.g., 'announcements/')
 * @param {Function} onProgress - Progress callback (percentage, bytesUploaded, bytesTotal)
 * @returns {{promise: Promise<string>, abort: Function}} The public URL of the uploaded file
 */
export function uploadFileToS3(file, folder = 'images/', onProgress = null) {
  let abortFn = () => {};
  
  const promise = (async () => {
    try {
      // Step 1: Get presigned URL
      const presigned = await getPresignedUrl(file, folder);

      // Step 2: Upload directly to S3
      const { promise: uploadPromise, abort } = uploadToS3(presigned.upload_url, file, onProgress);
      abortFn = abort;
      
      const uploadResult = await uploadPromise;

      if (!uploadResult.success) {
        throw new Error('Upload failed');
      }

      // Step 3: Confirm upload
      await confirmUpload(
        presigned.file_key,
        file.name,
        file.type,
        file.size
      );

      return presigned.file_url;

    } catch (error) {
      console.error('S3 file upload error:', error);
      throw error;
    }
  })();

  return {
    promise,
    abort: () => abortFn()
  };
}

/**
 * Check if a file key belongs to a public folder
 * @param {string} fileKey - The R2 object key
 * @returns {boolean}
 */
export function isPublicAsset(fileKey) {
  if (!fileKey) return false;
  return PUBLIC_FOLDERS.some(folder => fileKey.startsWith(folder));
}

/**
 * Check if a file key belongs to a private folder
 * @param {string} fileKey - The R2 object key
 * @returns {boolean}
 */
export function isPrivateAsset(fileKey) {
  if (!fileKey) return false;
  return PRIVATE_FOLDERS.some(folder => fileKey.startsWith(folder));
}

/**
 * Extract the file key from a full R2 URL
 * @param {string} url - Full R2 URL
 * @returns {string|null} File key or null if not an R2 URL
 */
export function extractFileKey(url) {
  if (!url) return null;
  
  // Handle public r2.dev URLs
  if (url.includes('.r2.dev/')) {
    const parts = url.split('.r2.dev/');
    return parts[1] || null;
  }
  
  // Handle private endpoint URLs
  if (url.includes('.r2.cloudflarestorage.com/')) {
    const parts = url.split('.r2.cloudflarestorage.com/');
    if (parts[1]) {
      // Remove bucket name prefix if present
      const pathParts = parts[1].split('/');
      if (pathParts.length > 1) {
        pathParts.shift(); // Remove bucket name
        return pathParts.join('/');
      }
    }
  }
  
  return null;
}

/**
 * Get the public URL for a public asset
 * @param {string} fileKey - The R2 object key
 * @returns {string} Public r2.dev URL
 */
export function getPublicUrl(fileKey) {
  if (!R2_PUBLIC_URL) {
    console.error('R2_PUBLIC_URL is not configured. Cannot generate public URL.');
    return null;
  }
  return `${R2_PUBLIC_URL}/${fileKey}`;
}

/**
 * Get the appropriate URL for an asset based on access pattern
 * - Public assets: Returns direct r2.dev URL
 * - Private assets: Must call getPresignedDownloadUrl() to get signed URL
 * 
 * @param {string} fileKeyOrUrl - File key or full URL
 * @param {Object} options - Options
 * @param {string} options.courseId - Course ID for enrollment verification (for private videos)
 * @returns {Promise<{url: string, isPublic: boolean}>}
 */
export async function getAssetUrl(fileKeyOrUrl, options = {}) {
  // Extract file key if a full URL was provided
  let fileKey = fileKeyOrUrl;
  if (fileKeyOrUrl.includes('://')) {
    fileKey = extractFileKey(fileKeyOrUrl) || fileKeyOrUrl;
  }
  
  // Public assets - return direct URL
  if (isPublicAsset(fileKey)) {
    return {
      url: getPublicUrl(fileKey),
      isPublic: true
    };
  }
  
  // Private assets - get presigned URL
  try {
    const result = await getPresignedDownloadUrl(fileKey, options.courseId);
    return {
      url: result.download_url,
      isPublic: false,
      expiresIn: result.expires_in
    };
  } catch (error) {
    console.error('Failed to get presigned URL:', error);
    throw error;
  }
}

/**
 * Get optimized S3 URL (placeholder for future CDN integration)
 * @param {string} url - Original S3 URL
 * @param {Object} options - Transformation options (for future use)
 * @returns {string} URL (unchanged for now, can add CloudFront later)
 */
export function getOptimizedUrl(url, options = {}) {
  // For now, return the URL as-is
  // In the future, this can be modified to use CloudFront or other CDN
  return url;
}


/**
 * Upload a certificate to R2 using presigned URL (direct browser upload)
 * @param {File} file - The certificate file (PDF or image)
 * @param {string} title - Certificate title
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<{certificate: Object}>} The created certificate record
 */
export async function uploadCertificateToR2(file, title, onProgress = null) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  // Validate file type
  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error('File must be a PDF or image (JPEG, PNG, WebP)');
  }

  // Validate file size (10MB max)
  if (file.size > 10 * 1024 * 1024) {
    throw new Error('File must be less than 10MB');
  }

  try {
    // Step 1: Get presigned URL for certificate upload
    const presignedParams = new URLSearchParams({
      filename: file.name,
      content_type: file.type,
      file_size: file.size.toString()
    });

    const presignedResponse = await fetch(
      `${API_URL}/api/teacher/certificates/presigned-url?${presignedParams}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!presignedResponse.ok) {
      const error = await presignedResponse.json();
      throw new Error(error.detail || 'Failed to get upload URL');
    }

    const presigned = await presignedResponse.json();

    // Step 2: Upload directly to R2
    const uploadResult = await uploadToR2WithProgress(presigned.upload_url, file, onProgress);

    if (!uploadResult.success) {
      throw new Error('Upload failed');
    }

    // Step 3: Confirm upload and create certificate record
    const confirmParams = new URLSearchParams({
      file_key: presigned.file_key,
      cert_id: presigned.cert_id,
      title: title,
      original_filename: file.name,
      content_type: file.type,
      file_size: file.size.toString()
    });

    const confirmResponse = await fetch(
      `${API_URL}/api/teacher/certificates/confirm?${confirmParams}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!confirmResponse.ok) {
      const error = await confirmResponse.json();
      throw new Error(error.detail || 'Failed to confirm upload');
    }

    return confirmResponse.json();

  } catch (error) {
    console.error('Certificate upload error:', error);
    throw error;
  }
}


/**
 * Upload file to R2 using presigned URL with progress tracking
 * @param {string} presignedUrl - The presigned PUT URL
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<{success: boolean}>}
 */
function uploadToR2WithProgress(presignedUrl, file, onProgress = null) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ success: true });
      } else {
        console.error('R2 upload failed:', xhr.status, xhr.responseText);
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}

